import React, { ReactElement } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import axios from "axios";

import { createAlert as createAlertAction } from "actions/alerts";
import { AlertType } from "actions/alerts/types";
import { Dispatch } from "actions/types";
import { State } from "reducers/types";
import getAPIRoot from "services/api-root";
import { validatePasswordAlphaNum, validatePasswordSpecialChar } from "services/validations";
import { VALID_PASSWORD_LENGTH } from "constants/passwordGuidelines";
import { Client } from "resourceModels";
import { getResource } from "selectors/resources";

import SvgIcon from "components/Common/SvgIcon";
import Footer from "components/Common/Footer";
import Button from "components/Common/Button";
import InputPassword from "components/Common/InputPassword";
import PasswordGuidelines from "components/Common/PasswordGuidelines";
import colors from "stylesheets/utilities/colors.scss";

import "./style.scss";
import { loginUser as loginUserAction } from "actions";

interface PropsFromState {
  client: Client;
}

interface PropsFromDispatch {
  createAlert: typeof createAlertAction;
  loginUser: typeof loginUserAction;
}

type PasswordSetProps = PropsFromState & PropsFromDispatch & {
  match: { params: { token: string } };
};

interface PasswordSetState {
  password: string;
  passwordConfirm: string;
  email: string;
  hasEmailChangedSuccessfully: boolean;
}

/**
 * This is the new Password Reset Page!
 * it should validate a valid token, and allow the user to set their new password.
 */
class PasswordSet extends React.Component<PasswordSetProps, PasswordSetState> {
  onChangePasswordFormBound = this.onChangePasswordForm.bind(this);
  onLoginUserBound = this.onLoginUser.bind(this);

  state: PasswordSetState = {
    password: "",
    passwordConfirm: "",
    email: "",
    hasEmailChangedSuccessfully: false
  };

  /**
   * Event handlers
   */

  onLoginUser(): void {
    const { loginUser } = this.props;
    const { email, password } = this.state;

    loginUser(email, password);
  }

  onChangePasswordForm(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    this.changePasswordRequest();
  }

  /**
   * Getters
   */

  /** returns true if and only if the new password is valid */
  get isNewPasswordValid(): boolean {
    const { password, passwordConfirm } = this.state;
    const passwordsMatch = password === passwordConfirm;
    const passwordPassesQualityValidation = (
      validatePasswordAlphaNum(password) &&
      validatePasswordSpecialChar(password) &&
      password.length >= VALID_PASSWORD_LENGTH
    );

    return passwordsMatch && passwordPassesQualityValidation;
  }

  /** Get the token from the url params */
  get token(): string {
    const { match } = this.props;

    return match.params.token;
  }

  /**
   * Methods
   */

  /**
   * This function makes an attempt to reset the password.
   * Upon success, we show them the success screen and prompt them to log in.
   */
  changePasswordRequest(): Promise<void> {
    const { createAlert } = this.props;
    const { password } = this.state;

    return axios({
      method: "post",
      url: "/users/password/set/",
      data: {
        password,
        token: this.token
      },
      baseURL: getAPIRoot(),
      headers: {
        "Content-Type": "application/json"
      }
    }).then((res) => {
      this.setState({
        hasEmailChangedSuccessfully: true,
        email: res.data.email
      });
    }).catch((err) => {
      createAlert({
        message: err.response.data.message,
        alertType: AlertType.ERROR
      });
    });
  }

  /**
   * Render methods
   */

  /**
   * This method renders the input field for the desired password.
   */
  renderPasswordForm(): ReactElement {
    const { client } = this.props;
    const { password, passwordConfirm } = this.state;

    let formDescription = "Set a password to use on the bot. All data you enter is kept secure and private.";

    // set form description to Client specific version if client exisit
    if (client) {
      formDescription = `Set a password to use on the ${client.handle} bot. All data you enter is kept secure and private.`;
    }

    return (
      <>
        <div className="PasswordSet__password-form__page-header">
          <h1 className="PasswordSet__heading">Set Your Password</h1>
          <p>{formDescription}</p>
        </div>

        {/* Password reset form */}
        <form onSubmit={this.onChangePasswordFormBound}>
          <div className="PasswordSet__input-section">
            <label
              className="g-input__label PasswordSet__label"
              htmlFor="PasswordSet__new-pasword"
            >
              Password
            </label>
            <InputPassword
              inputId="PasswordSet__password"
              value={password}
              customClassName="PasswordSet__input"
              onPasswordChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                this.setState({ password: e.target.value });
              }}
            />
          </div>
          <div className="PasswordSet__input-section">
            <label
              className="g-input__label PasswordSet__label"
              htmlFor="PasswordSet__input--again"
            >
              Confirm Password
            </label>
            <InputPassword
              inputId="PasswordSet__password--again"
              value={passwordConfirm}
              customClassName="PasswordSet__input PasswordSet__input--again"
              onPasswordChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                this.setState({ passwordConfirm: e.target.value });
              }}
            />
          </div>
          <section className="PasswordSet__validations">
            <PasswordGuidelines password={password} passwordConfirm={passwordConfirm} />
          </section>
          <Button
            type="submit"
            text="Set Password"
            title="Set Password"
            customClassName="PasswordSet__button"
            disabled={!this.isNewPasswordValid}
          />
        </form>
      </>
    );
  }

  /**
   * The success page to be shown after we have successfully set our password
   */
  renderSuccessPageContent(): ReactElement {
    const { email } = this.state;

    return (
      <>
        <div className="PasswordSet__password-form__page-header">
          <SvgIcon
            customClassName="PasswordSet__password-form__success-icon"
            icon="CircleCheckmark"
            fillColor={colors.colorUIGood}
            height={36}
          />
          <h1 className="PasswordSet__heading">Password Set</h1>
          <p>{`You have successfully set the password for ${email}.`}</p>
        </div>
        <Button
          icon="SignIn"
          text="Continue to Dashboard"
          title="Continue to Dashboard"
          customClassName="PasswordSet__button"
          onClick={this.onLoginUserBound}
        />
      </>
    );
  }

  render(): ReactElement {
    const { hasEmailChangedSuccessfully } = this.state;

    return (
      <>
        <div className="PasswordSet g-view">
          <div className="PasswordSet__password-form g-form__block">
            {hasEmailChangedSuccessfully
              ? this.renderSuccessPageContent()
              : this.renderPasswordForm()}
          </div>
        </div>
        <Footer />
      </>
    );
  }
}

function mapStateToProps(state: State): PropsFromState {
  return {
    client: getResource(state, "client")
  };
}

function mapDispatchToProps(dispatch: Dispatch): PropsFromDispatch {
  return bindActionCreators({
    createAlert: createAlertAction,
    loginUser: loginUserAction
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(PasswordSet);
